import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { capitalizeFirst, cn } from "@/lib/utils";
import { CATEGORIES } from "@/utils/constant";
import { Briefcase, Clock, MapPin, Phone } from "lucide-react";

interface ProfileInfoProps {
  isEditing: boolean;
  formData: {
    fullName: string;
    designation: string;
    location: string;
    category: string;
    experience: string;
    phone: string;
    availability: "available" | "offline";
  };
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleSettingChange: (key: string, value: any) => void;
}

export const ProfileInfo = ({
  isEditing,
  formData,
  handleInputChange,
  handleSettingChange
}: ProfileInfoProps) => {
  return (
    <div className="text-center mb-6">
      {isEditing ? (
        <div className="flex items-center justify-center mt-2">
          <Input
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            className="text-center w-auto"
            placeholder="Your full name"
            required
          />
        </div>
      ) : (
        formData.fullName && (
          <div className="flex items-center justify-center mt-2 text-gray-600">
              <h2 className="text-2xl font-bold text-gray-900">
                {capitalizeFirst(formData.fullName).split(' ')[0]}
              </h2>
          </div>
        )
      )}
      {isEditing ? (
        <Input
          name="designation"
          value={formData.designation}
          onChange={handleInputChange}
          className="text-center mt-2"
          placeholder="Your professional designation"
          required
        />
      ) : (
        <p className="text-gray-600 capitalize">{formData.designation}</p>
      )}

      {isEditing ? (
        <div className="flex items-center justify-center mt-2">
          <Briefcase className="w-4 h-4 text-gray-500 mr-1" />
          <Select
            onValueChange={(value) => handleSettingChange("category", value)}
            value={formData.category}
          >
            <SelectTrigger
              id="category"
              className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300 w-auto"
            >
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : (
        formData.category && (
          <div className="flex items-center justify-center mt-2 capitalize">
            <Badge variant="secondary">{formData.category}</Badge>
          </div>
        )
      )}

      {isEditing ? (
        <div className="flex items-center justify-center mt-2">
          <Clock className="w-4 h-4 text-gray-500 mr-1" />
          <Input
            name="experience"
            value={formData.experience}
            onChange={handleInputChange}
            className="text-center w-auto"
            placeholder="Years of experience"
          />
        </div>
      ) : (
        formData.experience && (
          <div className="flex items-center justify-center mt-2 text-gray-600">
            <Clock className="w-4 h-4 text-gray-500 mr-1" />
            {formData.experience} years of experience
          </div>
        )
      )}

      {isEditing && (
        <div className="flex items-center justify-center mt-2">
          <Phone className="w-4 h-4 text-gray-500 mr-1" />
          <Input
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="text-center w-auto"
            placeholder="Your phone number"
          />
        </div>
      )}

      {isEditing ? (
        <div className="flex items-center justify-center mt-2">
          <MapPin className="w-4 h-4 text-gray-500 mr-1" />
          <Input
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            className="text-center w-auto"
            placeholder="Your location"
          />
        </div>
      ) : (
        formData.location && (
          <div className="flex items-center justify-center mt-2 text-gray-600">
            <MapPin className="w-4 h-4 text-gray-500 mr-1 capitalize" />
            {formData.location}
          </div>
        )
      )}

      {isEditing ? (
        <div className="flex items-center justify-center mt-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium mx-2" htmlFor="availability">
              Availability
            </Label>
            <Switch
              id="availability"
              checked={formData?.availability === "available"}
              onCheckedChange={(checked) =>
                handleSettingChange("availability", checked ? "available" : "offline")
              }
            />
          </div>
        </div>
      ) : (
        formData.availability && (
          <div className={cn("flex items-center justify-center mt-2 text-gray-600 capitalize")}>
            {formData.availability}
          </div>
        )
      )}
    </div>
  );
};
